// src/components/NavBar.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import logoSvg from '../assets/logo.svg';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: ${({ scrolled }) => scrolled ? '0.75rem 0' : '1.5rem 0'};
  z-index: var(--z-nav);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.5);
  background: transparent;
  
  /* Glass Filter Layer - blur + saturation (when scrolled) */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: ${({ scrolled }) => scrolled ? 1 : 0};
    backdrop-filter: blur(16px) saturate(180%) brightness(1.05);
    -webkit-backdrop-filter: blur(16px) saturate(180%) brightness(1.05);
    background: rgba(255, 255, 255, 0.12);
    transition: opacity 0.4s ease;
    z-index: -2;
  }
  
  /* Glass Specular Layer - light reflections (when scrolled) */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    opacity: ${({ scrolled }) => scrolled ? 1 : 0};
    box-shadow: 
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(0, 0, 0, 0.05),
      0 8px 32px rgba(0, 0, 0, 0.12);
    pointer-events: none;
    transition: opacity 0.4s ease;
    z-index: -1;
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  z-index: 5;

  img {
    height: 26px;
    width: auto;
    transition: filter var(--transition-fast);
    /* Keep white on glass background with subtle glow */
    filter: ${({ scrolled }) => scrolled
    ? 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.6)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
    : 'none'
  };
  }

  &:hover img {
    filter: ${({ scrolled }) => scrolled
    ? 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.8)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4)) brightness(1.1)'
    : 'brightness(0.9)'
  };
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  margin-left: var(--spacing-lg);
  font-weight: 600;
  color: var(--white);
  transition: all var(--transition-fast);
  position: relative;
  cursor: pointer;
  text-decoration: none;
  z-index: 5;
  text-shadow: ${({ scrolled }) => scrolled
    ? '0 2px 4px rgba(0, 0, 0, 0.3), 0 0 10px rgba(255, 255, 255, 0.3)'
    : 'none'};

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: var(--white);
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
    transition: width var(--transition-fast);
  }

  &:hover {
    color: var(--white);
    text-shadow: 0 0 15px rgba(255, 255, 255, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);

    &:after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ scrolled }) => scrolled ? 'var(--primary)' : 'var(--white)'};
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--primary);
  z-index: calc(var(--z-nav) + 1);
  padding: var(--spacing-lg);
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileNavLink = styled(Link)`
  margin: var(--spacing-md) 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--white);
  text-align: center;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--accent);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: none;
  border: none;
  color: var(--white);
  font-size: 1.5rem;
  cursor: pointer;
`;

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  return (
    <>
      <Nav scrolled={scrolled}>
        <NavContainer>
          <Logo
            scrolled={scrolled}
            to="/"
          >
            <img src={logoSvg} alt="XADevs" />
          </Logo>
          <NavLinks>
            <NavLink
              scrolled={scrolled}
              to="/#products"
            >
              Products
            </NavLink>
            <NavLink
              scrolled={scrolled}
              to="/#about"
            >
              About
            </NavLink>
            <NavLink
              scrolled={scrolled}
              to="/#contact"
            >
              Contact
            </NavLink>
            <NavLink
              scrolled={scrolled}
              to="/support"
            >
              Support
            </NavLink>
          </NavLinks>
          <MobileMenuButton
            scrolled={scrolled}
            onClick={toggleMobileMenu}
            aria-label="Open mobile menu"
          >
            <FiMenu />
          </MobileMenuButton>
        </NavContainer>
      </Nav>

      {/* Mobile Menu */}
      <MobileMenu
        initial="closed"
        animate={mobileMenuOpen ? "open" : "closed"}
        variants={menuVariants}
      >
        <CloseButton
          onClick={toggleMobileMenu}
          aria-label="Close mobile menu"
        >
          <FiX />
        </CloseButton>
        <MobileNavLink to="/#products" onClick={toggleMobileMenu}>Products</MobileNavLink>
        <MobileNavLink to="/#about" onClick={toggleMobileMenu}>About</MobileNavLink>
        <MobileNavLink to="/#contact" onClick={toggleMobileMenu}>Contact</MobileNavLink>
        <MobileNavLink to="/support" onClick={toggleMobileMenu}>Support</MobileNavLink>
      </MobileMenu>
    </>
  );
};

export default NavBar;
